import { type MapRef, type LayerProps } from 'react-map-gl-v7';
import { discoverColors } from '@discover-styles/colors';
import { CLUSTER_LAYER_ID, FILTER_CLUSTERS, RESTAURANT_LAYER_ID } from './consts';

// Cluster circle layer factory
const createClusterCircleLayer = (
  id: string,
  filter = FILTER_CLUSTERS,
  color = discoverColors.blue.DEFAULT
): LayerProps => ({
  id,
  type: 'circle',
  filter,
  paint: {
    'circle-color': color,
    'circle-radius': [
      'step',
      ['get', 'point_count'],
      20, // Default size for small clusters (2-4 points)
      5, // When point count reaches 5
      25, // Size for small-medium clusters
      10, // When point count reaches 10
      30, // Size for medium clusters
      20, // When point count reaches 20
      35, // Size for medium-large clusters
      40, // When point count reaches 40
      40, // Size for large clusters
      80, // When point count reaches 80
      45, // Size for very large clusters
    ],
    'circle-stroke-width': 2,
    'circle-stroke-color': 'white',
  },
});

// Cluster count text layer factory
const createClusterCountLayer = (
  id: string,
  filter = FILTER_CLUSTERS,
  textColor = 'white'
): LayerProps => ({
  id,
  type: 'symbol',
  filter,
  layout: {
    'text-field': '{point_count_abbreviated}',
    'text-size': 18,
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': textColor,
  },
});

// Create and export cluster layers
export const clusterLayer = createClusterCircleLayer('clusters');
export const clusterCountLayer = createClusterCountLayer('cluster-count');

/**
 * Setup all cluster-related events and configuration on a map
 * @param mapRef - Reference to the mapbox map instance
 */
export const setupClusters = (mapRef: MapRef): void => {
  // Configuration constants

  // inspect a cluster on click
  mapRef.on('click', CLUSTER_LAYER_ID, (e) => {
    const clusterFeatures = mapRef.queryRenderedFeatures(e.point, {
      layers: [CLUSTER_LAYER_ID],
    });
    const clusterFeature = clusterFeatures[0];
    const clusterId: number = clusterFeature.properties?.cluster_id;
    const source = mapRef.getSource('restaurants');
    if (source.type === 'geojson') {
      source.getClusterExpansionZoom(clusterId, (err, zoom: number) => {
        if (err) {
          return;
        }

        function isLngLatLike(array: number[]): array is [number, number] {
          return array.length === 2;
        }

        if (
          clusterFeature.geometry.type === 'Point' &&
          isLngLatLike(clusterFeature.geometry.coordinates)
        ) {
          mapRef.easeTo({
            center: clusterFeature.geometry.coordinates,
            zoom,
          });
        }
      });
    }
  });

  // show pointer cursor on hover for clusters
  mapRef.on('mouseenter', CLUSTER_LAYER_ID, () => {
    mapRef.getCanvas().style.cursor = 'pointer';
  });
  mapRef.on('mouseleave', CLUSTER_LAYER_ID, () => {
    mapRef.getCanvas().style.cursor = '';
  });

  // Also add pointer for restaurant layer to maintain the same behavior
  mapRef.on('mouseenter', RESTAURANT_LAYER_ID, () => {
    mapRef.getCanvas().style.cursor = 'pointer';
  });
  mapRef.on('mouseleave', RESTAURANT_LAYER_ID, () => {
    mapRef.getCanvas().style.cursor = '';
  });
};
